import { Component, Inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  ActivityBodyInterface,
  ActivityFileTypeEnum,
  ActivityInterface,
  FileTypeEnum,
  MediaTypeEnum,
} from '@sport-activities/sdk';
import { v4 as uuidv4 } from 'uuid';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';

import { FormGroupValueInterface } from '~/component/form/interfaces/form-group-value.interface';
import { CDN_ROOT } from '~/core/core.module';
import { base64toBlob } from '~/core/helpers/image/base64-to-blob/base64-to-blob.helper';
import { FormControlService } from '~/core/services/form/form-control.service';
import { RoutingInterface } from '~/core/services/navigation/interfaces/routing.interface';
import { NavigationService } from '~/core/services/navigation/navigation.service';
import { TitleService } from '~/core/services/title/title.service';
import { AbstractOrganizerActivityCreationFormPageDirective } from '~/organizer/activity-creation/components/shared/form-wrapper/classes/abstract-activity-creation-form-page';
import { NAVIGATION_ROUTING } from '~/organizer/shared/components/shared/navigation-buttons/navigation-buttons.component';
import { OrganizerSharedPageContainerTipConfigInterface } from '~/organizer/shared/components/shared/page-container/interfaces/tip.config.interface';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';
import {
  getRatio,
  mediaResolutions,
} from '~/organizer/shared/helpers/media/media.helper';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';

@Component({
  selector: 'app-organizer-activity-creation-photos',
  templateUrl: './photos.component.html',
  styleUrls: [
    '../../../../../organizer/shared/components/shared/form-wrapper/form-wrapper.component.scss',
    './photos.component.scss',
  ],
})
export class OrganizerActivityCreationPagePhotosComponent extends AbstractOrganizerActivityCreationFormPageDirective {
  protected readonly pageTitleKey =
    'seo.organizer.activity_creation.photos.title';
  public readonly tipsConfig: OrganizerSharedPageContainerTipConfigInterface[] =
    [
      {
        descriptionKey: 'activity_creation.photos.tips.description',
        headerKey: 'activity_creation.photos.tips.header',
      },
    ];

  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly toggleNameEnum = ToggleNameEnum;

  public displayValidationError = false;

  public photoCdnPath: string;

  constructor(
    @Inject(CDN_ROOT) private readonly cdnRoot: string,
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly activityService: OrganizerSharedActivityService,
    protected readonly formBuilder: FormBuilder,
    protected readonly navigationService: NavigationService,
    protected readonly organizationService: OrganizerSharedOrganizationService,
    @Inject(NAVIGATION_ROUTING)
    protected readonly navigationRouting: RoutingInterface[],
    protected readonly titleService: TitleService,
    private readonly formControlService: FormControlService
  ) {
    super(
      activatedRoute,
      activityService,
      formBuilder,
      navigationService,
      organizationService,
      navigationRouting,
      titleService
    );
  }

  public get imageAspectRatio(): number {
    return getRatio(ActivityMediaResolutionEnum.LARGE);
  }

  public get isFormValid(): boolean {
    return this.formGroup.valid;
  }

  private getDefaultImageUrl(): string {
    return `${this.cdnRoot}/common/default-618x412.jpg`;
  }

  protected buildActivityData(): Partial<ActivityInterface> {
    return {
      image: {
        name: 'main',
        url: this.photoCdnPath,
        type: 'image' as MediaTypeEnum,
      },
    };
  }

  protected buildDataBeforeSubmit(): Partial<ActivityBodyInterface> {
    return {
      image: this.photoCdnPath,
    };
  }

  protected buildValidationConfig(): void {
    const activity = this.activityService.activity;

    this.validationConfig = {
      photo: [
        activity.getMediaUrlByType(
          'MAIN_MEDIA' as ActivityFileTypeEnum,
          ActivityMediaResolutionEnum.LARGE
        ) ||
          activity.image?.url ||
          '',
        Validators.required,
      ],
    };
  }

  public clickNextNavigationButton(): void {
    if (this.formGroup.invalid) {
      this.displayValidationError = true;
    }
  }

  public errorMessages(inputName: string): string[] {
    const control = this.formGroup.get(inputName);

    return (
      this.displayValidationError &&
      this.formControlService.getErrorMessages(control, inputName)
    );
  }

  public submit(
    formGroupValue: FormGroupValueInterface
  ): void | Promise<boolean> {
    if (!this.isFormValid) {
      this.displayValidationError = true;
      return;
    }

    const activity = this.activityService.activity;
    const photoValue = formGroupValue.photo;

    // If this is an existing picture, no call to the media API should be done
    if (photoValue.startsWith('http')) {
      if (photoValue === activity.image?.url) {
        return this.redirectAfterSubmit();
      }

      this.photoCdnPath = photoValue;

      super.submit(formGroupValue);

      return;
    }

    // If the user sends his own image
    this.activityService
      .addMedia(
        activity.sku,
        base64toBlob(photoValue),
        `${activity?.sku}_${uuidv4()}`,
        ['MAIN_MEDIA' as FileTypeEnum],
        [
          mediaResolutions[ActivityMediaResolutionEnum.LARGE],
          mediaResolutions[ActivityMediaResolutionEnum.LARGE_CARD],
          mediaResolutions[ActivityMediaResolutionEnum.CARD],
          mediaResolutions[ActivityMediaResolutionEnum.HP_CARD],
          mediaResolutions[ActivityMediaResolutionEnum.ICON],
        ]
      )
      .subscribe((media) => {
        this.photoCdnPath = media.url;

        super.submit(formGroupValue);
      });
  }

  public useDefaultPhoto(): void {
    const photoControl = this.formGroup.get('photo');
    photoControl.setValue(this.getDefaultImageUrl());
    photoControl.updateValueAndValidity();
  }
}
